import { Button } from "@/components/ui/button";
import { Download, Zap, Shield, Sparkles, Chrome, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import { useTheme } from "next-themes";
import { Sun, Moon } from "lucide-react";
import PageFooter from "@/components/PageFooter";
import { useState, useEffect } from "react";

// Chrome Web Store URL - update this when extension is published
const CHROME_STORE_URL = "https://chromewebstore.google.com/detail/annbiofboodalhjeopdfabfjbkifbghj";

const screenshots = [
  { src: "/screenshots/screenshot-1.jpg", alt: "Untabbed.to extension capturing tabs" },
  { src: "/screenshots/screenshot-2.jpg", alt: "Beautiful tab collections with thumbnails" },
  { src: "/screenshots/screenshot-3.jpg", alt: "Search and filter your saved tabs" },
  { src: "/screenshots/screenshot-4.jpg", alt: "Manage and organize tab collections" },
];

const Landing = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const handleChromeInstall = (e: React.MouseEvent) => {
    e.preventDefault();
    // TODO: Replace with actual Chrome Web Store URL when published
    window.open(CHROME_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">
      {/* Animated background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-primary/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-purple-300/20 dark:bg-purple-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-300/20 dark:bg-pink-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group" aria-label="Home">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-purple-600 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                <img src={logoDark} alt="untabbed.to logo" className="h-8 w-8 dark:hidden relative" />
                <img src={logo} alt="untabbed.to logo" className="h-8 w-8 hidden dark:block relative" />
              </div>
              <h1 className="text-3xl font-display font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent tracking-tight">
                untabbed.to
              </h1>
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 hidden md:inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 border border-primary/20 text-xs font-medium hover:scale-105 transition-transform duration-300 cursor-default shadow-lg backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
              <span className="bg-gradient-to-r from-primary via-purple-600 to-primary bg-clip-text text-transparent">
                Transform Your Browsing Experience
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`}
              aria-pressed={resolvedTheme === "dark"}
              className="hover:bg-primary/10 transition-all duration-300"
            >
              {resolvedTheme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-12 md:py-16 relative">
        <div className="max-w-4xl mx-auto text-center space-y-6 relative z-10">
          <div className="relative">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-foreground leading-tight">
              Say Goodbye to
              <span className="block text-primary dark:bg-gradient-to-r dark:from-primary dark:via-purple-600 dark:to-pink-600 dark:bg-clip-text dark:text-transparent mt-2 dark:animate-gradient">
                Tab Chaos
              </span>
            </h1>
            {/* Glow effect behind text */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-purple-600/20 to-pink-600/20 blur-3xl -z-10 opacity-50"></div>
          </div>

          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Organize, save, and access your browser tabs effortlessly. Keep your workflow clean and your mind clear.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <Button
              size="lg"
              onClick={handleChromeInstall}
              aria-label="Install Untabbed extension from Chrome Web Store"
              className="relative group shadow-2xl text-lg h-14 px-8 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-purple-600 blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <Chrome className="mr-2 h-5 w-5 relative z-10" />
              <span className="relative z-10">Add to Chrome</span>
              <ArrowRight className="ml-2 h-5 w-5 relative z-10 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Link to="/demo">
              <Button variant="outline" size="lg" className="text-lg h-14 px-8 hover:bg-primary/5 hover:border-primary/50 hover:shadow-xl transition-all duration-300 backdrop-blur-sm">
                Try Demo
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground pt-2">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Free
            </span>
            <span>•</span>
            <span className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Privacy first
            </span>
          </div>
        </div>
      </section>

      {/* Screenshots Slideshow Section */}
      <section className="container mx-auto px-6 py-8 relative">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-8">
            See It In Action
          </h2>

          <div className="relative group">
            {/* Main slideshow container */}
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border border-border/30">
              {/* Screenshots */}
              {screenshots.map((screenshot, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={screenshot.src}
                    alt={screenshot.alt}
                    className="w-full h-full object-contain bg-muted"
                  />
                </div>
              ))}

              {/* Navigation arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-border/30 hover:border-primary/50"
                aria-label="Previous screenshot"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-border/30 hover:border-primary/50"
                aria-label="Next screenshot"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            {/* Slide indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {screenshots.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'w-8 bg-primary'
                      : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Caption */}
            <p className="text-center text-muted-foreground mt-4 text-sm">
              {screenshots[currentSlide].alt}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-20 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-center mb-4">
            Why Choose untabbed.to?
          </h2>
          <p className="text-center text-muted-foreground text-lg mb-16 max-w-2xl mx-auto">
            Experience the perfect blend of speed, security, and simplicity
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group glass-card p-8 rounded-2xl border border-border/30 hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4 group-hover:text-primary transition-colors">Lightning Fast</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Save and restore your tab collections instantly. No more hunting through dozens of open tabs.
                </p>
              </div>
            </div>

            <div className="group glass-card p-8 rounded-2xl border border-border/30 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-7 w-7 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">Privacy First</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Your tabs stay local by default. Optional cloud sharing with full control. We never sell your data.
                </p>
              </div>
            </div>

            <div className="group glass-card p-8 rounded-2xl border border-border/30 hover:border-pink-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-pink-500/20 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Sparkles className="h-7 w-7 text-pink-600 dark:text-pink-400" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">Smart Organization</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Drag, drop, and organize your tabs effortlessly. Create collections for work, research, or fun.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            Get Started in Seconds
          </h2>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-display font-bold mb-2">Install the Extension</h3>
                <p className="text-muted-foreground">
                  Add untabbed.to to your browser with one click. Available for Chrome and other Chromium-based browsers.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-display font-bold mb-2">Save Your Tabs</h3>
                <p className="text-muted-foreground">
                  Click the extension icon and save all your current tabs into a collection. Name it and organize it your way.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-display font-bold mb-2">Access Instantly</h3>
                <p className="text-muted-foreground">
                  Your collections are saved in your browser, ready to open anytime. No internet required, completely private.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                4
              </div>
              <div>
                <h3 className="text-xl font-display font-bold mb-2">Share Optionally</h3>
                <p className="text-muted-foreground">
                  Want to collaborate? Share a collection with colleagues or friends with a single click. You decide what to share.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto relative">
          {/* Glow effects */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-purple-600/20 to-pink-600/20 blur-3xl opacity-30"></div>

          <div className="relative glass-card rounded-3xl p-12 md:p-16 text-center border border-border/30 shadow-2xl backdrop-blur-xl overflow-hidden">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
                Ready to Declutter Your Browser?
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
                Join thousands of users who have already transformed their browsing experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  size="lg"
                  onClick={handleChromeInstall}
                  aria-label="Install Untabbed extension from Chrome Web Store"
                  className="relative group shadow-2xl text-lg h-16 px-10 bg-gradient-to-r from-primary via-purple-600 to-primary hover:from-primary/90 hover:via-purple-600/90 hover:to-primary/90 transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-600 to-primary blur-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
                  <Download className="mr-2 h-5 w-5 relative z-10" />
                  <span className="relative z-10">Get Started Free</span>
                </Button>
                <Link to="/privacy-policy">
                  <Button variant="ghost" size="lg" className="text-lg h-16 px-10 hover:bg-primary/10 transition-all duration-300">
                    <Shield className="mr-2 h-5 w-5" />
                    View Privacy Policy
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-muted-foreground mt-8 flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                No signup required • Instant access
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default Landing;
